/**
 * Per-workflow color coding.
 *
 * Gives each workflow a stable, recognizable accent color so users can associate a color
 * with a workflow at a glance (helping avoid filling a case for the wrong program/workflow).
 *
 * Color is an ADDITIVE cue only — callers must always keep the workflow's icon + text too,
 * since color alone is not accessible (red-green color blindness) and this is a HIPAA product
 * where a misread means wrong-program data entry.
 *
 * Sourcing:
 *  1. If the workflow has an explicit `color` naming a palette entry (a future proto field),
 *     that color is used — so authored colors become a drop-in upgrade with no caller changes.
 *  2. Otherwise a color is derived deterministically from the workflow `id`, so the same
 *     workflow always renders the same color across sessions.
 */

/**
 * Curated, categorical palette. Full class strings (not interpolated) so Tailwind's content
 * scanner keeps them. Each entry tints border + background and sets a readable foreground in
 * both light and dark themes. Hues are chosen to be visually distinct from one another.
 */
const WORKFLOW_COLORS = {
  sky: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  teal: 'border-teal-500/40 bg-teal-500/10 text-teal-700 dark:text-teal-300',
  emerald: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  amber: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  orange: 'border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300',
  rose: 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300',
  violet: 'border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300',
  fuchsia: 'border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300',
} as const;

type WorkflowColorKey = keyof typeof WORKFLOW_COLORS;

const COLOR_KEYS = Object.keys(WORKFLOW_COLORS) as WorkflowColorKey[];

/** Minimal shape needed for color selection. `color` is a forward-looking, optional field. */
export interface WorkflowColorInput {
  id?: string;
  color?: string;
}

/** Stable, order-independent string hash (deterministic across sessions). */
function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/**
 * Returns the Tailwind class string for a workflow's accent color: an explicitly assigned
 * palette color when present, otherwise one derived deterministically from the workflow id.
 */
export function getWorkflowColorClasses(workflow?: WorkflowColorInput | null): string {
  // 1. Prefer an explicitly-assigned palette color (future proto field).
  const explicit = workflow?.color;
  if (explicit && explicit in WORKFLOW_COLORS) {
    return WORKFLOW_COLORS[explicit as WorkflowColorKey];
  }

  // 2. Fall back to a deterministic color derived from the workflow id.
  const key = workflow?.id ?? '';
  const index = key ? hashString(key) % COLOR_KEYS.length : 0;
  return WORKFLOW_COLORS[COLOR_KEYS[index]];
}
